/**
 * Width-only desktop check.
 * Mirrors SCSS breakpoint $screen-lg-min (1025px).
 * Use this when you only care about simple width separation.
 *
 * @returns {boolean} true if viewport width is at or above the large (desktop) threshold
 */
export function isDesktop(): boolean {
  return window.innerWidth >= 1025; // $screen-lg-min
}

/**
 * SCSS media query token ($mq-medium-down-ls):
 *  (max-width: 1024px) and (max-aspect-ratio: 16/9)
 * When this matches the layout is considered "constrained" (treated as non‑desktop for
 * certain interactions / UI density adjustments).
 */
const MEDIUM_DOWN_LS_QUERY = "(max-width: 1024px) and (max-aspect-ratio: 16/9)";

/**
 * Layout-aware desktop check aligned with SCSS logic including the
 * medium-down landscape constraint.
 *
 * Current rule:
 *  - If $mq-medium-down-ls DOES NOT match => treat as desktop
 *  - (So any viewport that is NOT simultaneously <=1024px wide AND <=16/9 aspect ratio
 *    is considered desktop.)
 *
 * Note: This means very wide but narrower-than-1025px viewports (e.g. 900px width on an
 * ultra‑wide monitor with aspect ratio >16/9) will return true (desktop). If you intend
 * to also require the width threshold, change the implementation to:
 *    return window.innerWidth >= 1025 && !window.matchMedia(MEDIUM_DOWN_LS_QUERY).matches;
 *
 * @returns {boolean} true if layout should use "desktop" interaction patterns
 */
export function isLayoutDesktop(): boolean {
  return !window.matchMedia(MEDIUM_DOWN_LS_QUERY).matches;
}
